import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslate } from '@tolgee/react';
import { AppRoute } from '@agentselly/immodossier-shared';
import { useLoaderQuery } from '~/helper/useLoaderQuery';
import { useAppParams } from '~/helper/useAppParams';
import { LoadingWidget } from '~/components/widget/base/LoadingWidget';
import { ErrorWidget } from '~/components/widget/base/ErrorWidget';
import { RenderWidgets } from '~/components/widget/RenderWidgets';
import { cockpitCalendarWidgets } from '~/config/widgets/cockpit/cockpitCalendarWidgets';
import { useAuthz } from '~/context/AuthzContext';
import {
  ContactActions,
  EmptyBlock,
  fmtTime,
  Ico,
  Kpi,
  PageHead,
  V3Page,
} from '~/components/cockpit/v3';
import type {
  CockpitEvent,
  WidgetProps,
} from '~/components/cockpit/cockpitData';

export const Route = createFileRoute(
  AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_CALENDAR,
)({
  component: CalendarPage,
});

type Tab = 'up' | 'past' | 'bk';
type Ev = CockpitEvent & { readonly interestedStatus?: string | null };

const ts = (e: Ev): number => new Date(e.date).getTime();

const longDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('de-CH', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

function DateBlock(props: { readonly iso: string }): React.ReactElement {
  const d = new Date(props.iso);
  const part = (o: Intl.DateTimeFormatOptions): string =>
    d.toLocaleDateString('de-CH', o).replace('.', '');
  return (
    <span
      className="dt"
      aria-hidden="true"
    >
      <span>{part({ weekday: 'short' })}</span>
      <b>{d.getDate()}</b>
      <span>{part({ month: 'short' })}</span>
    </span>
  );
}

function CalendarPage(): React.ReactElement {
  const params = useAppParams(Route);
  const { t } = useTranslate();
  const { atLeast } = useAuthz();
  const isAdvisor = atLeast('partner');
  const [
    tab,
    setTab,
  ] = useState<Tab>('up');

  const query = useLoaderQuery(
    AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_CALENDAR,
    { params },
  );

  if (query.isLoading) return <LoadingWidget />;
  if (query.isError || !query.data) return <ErrorWidget error={query.error} />;

  if (!query.data.v3Redesign) {
    const widgetProps = query.data?.widgetProps || {};
    const widgets = cockpitCalendarWidgets(widgetProps);
    return (
      <div className="h-full flex flex-col gap-y-6">
        <RenderWidgets widgets={widgets} />
      </div>
    );
  }

  const wp = (query.data.widgetProps || {}) as WidgetProps;
  const up: Ev[] = [
    ...(wp.cockpitCalendarEventsUpcomingWidget?.data ?? []),
  ].sort((a, b) => ts(a) - ts(b));
  const past: Ev[] = [...(wp.cockpitCalendarEventsPastWidget?.data ?? [])].sort(
    (a, b) => ts(b) - ts(a),
  );
  const bk = [
    ...up,
    ...past,
  ].filter((e) => e.booked);
  const next = up.find((e) => e.booked);

  const interest: Record<string, [string, string]> = {
    new: [
      t('cockpit_cal_int_interested', 'Interessiert'),
      'c-info',
    ],
    interested: [
      t('cockpit_cal_int_interested', 'Interessiert'),
      'c-info',
    ],
    opened: [
      t('cockpit_cal_int_opened', 'Exposé geöffnet'),
      'c-neu',
    ],
    offer: [
      t('cockpit_cal_int_offer', 'Angebot abgegeben'),
      'c-ok',
    ],
    notinterested: [
      t('cockpit_cal_int_lost', 'Kein Interesse'),
      'c-neu',
    ],
  };

  const head = (
    <PageHead
      odId="page-head"
      title={t('cockpit_cal_title', 'Kalender')}
      sub={t('cockpit_cal_sub', 'Besichtigungen und Termine')}
    />
  );

  if (up.length + past.length === 0) {
    return (
      <V3Page>
        {head}
        <EmptyBlock
          icon="cal"
          title={t('cockpit_cal_empty_title', 'Noch keine Besichtigungen')}
          text={t(
            'cockpit_cal_empty_text',
            'Sobald Ihr Objekt online ist und Interessenten einen Termin buchen, sehen Sie hier bevorstehende und vergangene Besichtigungen.',
          )}
        />
      </V3Page>
    );
  }

  const ev = (e: Ev, i: number): React.ReactElement => {
    const notFound = e.booked && !e.bookedBy;
    const int = e.interestedStatus ? interest[e.interestedStatus] : undefined;
    return (
      <div
        className={`ev${e.booked ? '' : ' free'}`}
        key={`${e.date}-${i}`}
        data-od-id={`event-${i}`}
      >
        <DateBlock iso={e.date} />
        <div className="bd">
          <span className="t">
            {t('cockpit_cal_visit', 'Besichtigung')} · {fmtTime(e.date)}
          </span>
          <span className="sr">{longDate(e.date)}</span>
          {!e.booked ? (
            <span className="mu">
              {t(
                'cockpit_cal_free_text',
                'Noch nicht gebucht – freier Besichtigungstermin',
              )}
            </span>
          ) : notFound ? (
            <>
              <span className="chip c-warn">
                <i />
                {t('cockpit_cal_nocontact', 'Kontakt nicht gefunden')}
              </span>
              <span className="xs mu">
                {t(
                  'cockpit_cal_nocontact_text',
                  'Die Buchung existiert, der Kontakt konnte in HubSpot nicht zugeordnet werden.',
                )}
              </span>
            </>
          ) : (
            <span className="sm">
              <b>{e.bookedBy}</b>
              {int ? (
                <>
                  {' · '}
                  <span className={`chip ${int[1]}`}>
                    <i />
                    {int[0]}
                  </span>
                </>
              ) : null}
            </span>
          )}
          {e.hostedBy ? (
            <span className="xs mu">
              <Ico
                name="user"
                size={12}
              />{' '}
              {t('cockpit_cal_host', 'Gastgeber')}:{' '}
              {e.hostedBy === 'internal'
                ? t('cockpit_cal_host_int', 'Agent Selly')
                : t('cockpit_cal_host_ext', 'Partner')}
            </span>
          ) : null}
          {isAdvisor && e.bookedBy ? (
            <ContactActions
              name={e.bookedBy}
              phone={e.bookedByPhone}
              email={e.bookedByEmail}
            />
          ) : null}
        </div>
      </div>
    );
  };

  const tabs: [Tab, string, Ev[], string][] = [
    [
      'up',
      t('cockpit_cal_tab_up', 'Bevorstehend'),
      up,
      t(
        'cockpit_cal_none_up',
        'Keine bevorstehenden Termine. Sobald Interessenten eine Besichtigung buchen, erscheint sie hier.',
      ),
    ],
    [
      'past',
      t('cockpit_cal_tab_past', 'Vergangen'),
      past,
      t('cockpit_cal_none_past', 'Noch keine vergangenen Termine.'),
    ],
    [
      'bk',
      t('cockpit_cal_tab_bk', 'Gebucht'),
      bk,
      t('cockpit_cal_none_bk', 'Noch keine Buchungen.'),
    ],
  ];
  const [
    ,
    ,
    shown,
    emptyText,
  ] = tabs.find((x) => x[0] === tab) ?? tabs[0];

  return (
    <V3Page>
      {head}
      {next ? (
        <section
          className="card hl"
          data-od-id="next-appointment"
          aria-labelledby="h-nx"
        >
          <p
            className="eyebrow"
            id="h-nx"
          >
            {t('cockpit_cal_next', 'Nächster gebuchter Termin')}
          </p>
          <div
            className="next"
            style={{ marginTop: 8 }}
          >
            <DateBlock iso={next.date} />
            <div style={{ minWidth: 0 }}>
              <p style={{ fontSize: '1.125rem', fontWeight: 600 }}>
                {longDate(next.date)}
              </p>
              <p className="mu">
                {fmtTime(next.date)} {t('cockpit_cal_oclock', 'Uhr')} ·{' '}
                {t('cockpit_cal_visit', 'Besichtigung')}
                {next.bookedBy
                  ? ` ${t('cockpit_cal_with', 'mit')} ${next.bookedBy}`
                  : ''}
              </p>
            </div>
          </div>
        </section>
      ) : null}

      <div
        className="grid g4"
        data-od-id="kpi-row"
      >
        <Kpi
          odId="kpi-upcoming"
          value={up.length}
          label={t('cockpit_cal_tab_up', 'Bevorstehend')}
        />
        <Kpi
          odId="kpi-upcoming-booked"
          value={up.filter((e) => e.booked).length}
          label={t('cockpit_cal_kpi_upcoming_booked', 'Davon gebucht')}
        />
        <Kpi
          odId="kpi-past"
          value={past.length}
          label={t('cockpit_cal_tab_past', 'Vergangen')}
        />
        <Kpi
          odId="kpi-booked"
          value={bk.length}
          label={t('cockpit_cal_kpi_booked', 'Buchungen gesamt')}
        />
      </div>

      <section
        className="card"
        data-od-id="calendar-events"
        data-widget="cockpitCalendarEventsWidget"
        aria-labelledby="h-ev"
      >
        <div
          className="sec"
          style={{ marginBottom: 12 }}
        >
          <h2 id="h-ev">{t('cockpit_cal_events', 'Termine')}</h2>
          <div
            className="tab"
            role="group"
            aria-label={t('cockpit_cal_filter', 'Termine filtern')}
            data-od-id="calendar-tabs"
          >
            {tabs.map(
              ([
                key,
                label,
                list,
              ]) => (
                <button
                  type="button"
                  key={key}
                  className={tab === key ? 'on' : ''}
                  aria-pressed={tab === key}
                  onClick={() => setTab(key)}
                >
                  {label} <span className="num mu">{list.length}</span>
                </button>
              ),
            )}
          </div>
        </div>
        {shown.length ? (
          shown.map(ev)
        ) : (
          <p
            className="sm mu"
            style={{ padding: '16px 0' }}
          >
            {emptyText}
          </p>
        )}
      </section>

      {isAdvisor ? null : (
        <div
          className="callout"
          data-od-id="privacy-note"
        >
          <Ico
            name="info"
            size={18}
          />
          <span>
            {t(
              'cockpit_cal_privacy',
              'Aus Datenschutzgründen sehen Sie nur den Namen der Interessenten. Telefon und E-Mail sind Ihrem Betreuer vorbehalten.',
            )}
          </span>
        </div>
      )}
    </V3Page>
  );
}
